/** Step 3a — 自定义活动名称 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAppSettings } from '../../components/providers/AppSettingsProvider';
import { loadAuthUser } from '../../services/auth';
import { createPageUrl } from '../../utils';
import { CheckInShell } from './components/CheckInShell';

const MAX_CHARS = 20;

/** 单个词：无空白；仅字母与数字（含中文等 \p{L}\p{N}） */
const CUSTOM_ACTIVITY_OK = /^[\p{L}\p{N}]+$/u;

/** 只保留第一个词中的字母/数字，最多 MAX_CHARS */
function normalizeInput(raw: string): string {
  const firstWord = raw.trim().split(/\s+/)[0] ?? '';
  const matched = firstWord.match(/[\p{L}\p{N}]/gu);
  if (!matched) return '';
  return matched.slice(0, MAX_CHARS).join('');
}

const CheckInEventCustomize: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useAppSettings();
  const moodTag = (location.state as { moodTag?: string } | null)?.moodTag;
  const [value, setValue] = useState('');

  const displayLen = useMemo(() => value.length, [value]);

  useEffect(() => {
    if (!loadAuthUser()) {
      navigate('/splash', { replace: true, state: { forceLogin: true } });
    }
  }, [navigate]);

  useEffect(() => {
    if (!moodTag) navigate('/check-in', { replace: true });
  }, [moodTag, navigate]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(normalizeInput(e.target.value));
  }, []);

  if (!moodTag) return null;

  const handleContinue = () => {
    if (!moodTag) return;
    const trimmed = value.trim();
    if (!trimmed) { toast.error(t('checkIn.customEmpty')); return; }
    if (!CUSTOM_ACTIVITY_OK.test(trimmed)) { toast.error(t('checkIn.customInvalid')); return; }
    navigate(createPageUrl('CheckInEventCustomizeIcon'), { state: { moodTag, customTitle: trimmed } });
  };

  return (
    <CheckInShell
      ctaLabel={t('checkIn.continue')}
      onCta={handleContinue}
      ctaDisabled={!value.trim()}
    >
      <h1 className="mt-6 text-center text-lg font-medium tracking-tight text-white">
        {t('checkIn.customTitle')}
      </h1>

      <div className="relative mt-8 w-full">
        <span className="pointer-events-none absolute -top-2 right-3 z-[2] rounded-full border border-white/35 bg-[#1a2f55]/90 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-white/90 shadow-sm backdrop-blur-md">
          {t('checkIn.customCharCount', { n: String(displayLen), max: String(MAX_CHARS) })}
        </span>
        <input
          type="text"
          value={value}
          onChange={onChange}
          maxLength={MAX_CHARS}
          placeholder={t('checkIn.customPlaceholder')}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className="w-full rounded-2xl border border-white/35 bg-white/[0.12] px-4 py-3.5 pr-16 text-[15px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] outline-none ring-0 backdrop-blur-xl backdrop-saturate-150 placeholder:text-white/40 focus:border-white/50"
          aria-label={t('checkIn.customPlaceholder')}
        />
      </div>
      <p className="mt-3 w-full text-center text-[11px] font-medium text-white/55">
        {t('checkIn.customHint')}
      </p>
    </CheckInShell>
  );
};

export default CheckInEventCustomize;
